import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { planInteriorGlowAnchor } from "./interiorGlowAnchor";
import {
	applyShadowFrustum,
	fitDirectionalShadowFrustum,
} from "./sunShadowFrustum";
import type {
	TimeOfDayValue,
	ViewerPresentationProfile,
	ViewerRuntimeProfile,
} from "./viewerConfig";
import {
	getTimeOfDayLightingTarget,
	TONE_MAPPING_EXPOSURE_COMPENSATION,
} from "./viewerConfig";

interface SceneLightingProps {
	interiorLighting?: boolean;
	runtimeProfile: ViewerRuntimeProfile;
	presentationProfile: ViewerPresentationProfile;
	timeOfDay: TimeOfDayValue;
	isTimeOfDayScrubbing?: boolean;
	/**
	 * World bounds of the shadow casters, re-measured by `SaunaModel`
	 * on every dirty-tracked rebuild. Null until the first build.
	 */
	modelBounds?: THREE.Box3 | null;
	/**
	 * Bumped on every rebuild. `modelBounds` is NOT: SceneCanvas keeps the
	 * previous box when the envelope is unchanged, so a bench or heater move
	 * inside the same shell would leave anything keyed on the bounds stale.
	 */
	modelRevision?: number;
	/**
	 * World Y of the ground the shadow lands on. Never 0 — plinth
	 * height, burial depth and terrain slope all push it down, and the
	 * far plane has to reach it.
	 */
	shadowReceiverY?: number;
}

/**
 * How far the key light may drift from its last shadow-camera fit
 * before the frustum is re-fitted, in metres. The time-of-day lerp
 * swings the key several metres — 8 m from midday to night on the high
 * tier — so a fit made for the target pose would leave the shadow
 * clipped for most of the transition. At 0.35 m that is ~20 refits per
 * full transition, inside the frame callback that is already running.
 */
const SHADOW_REFIT_DISTANCE_M = 0.35;

/**
 * The band `lerpPositionToTuple` treats as settled. The lerp is
 * exponential and never reaches zero, so the settling-frame refit has
 * to use the same epsilon or it fires on every frame of a held scrub.
 */
const LERP_SETTLED_DISTANCE_M = 0.002;

function lerpNum(current: number, target: number, alpha: number): number {
	return current + (target - current) * alpha;
}

function lerpColorToHex(
	color: THREE.Color,
	targetHex: number,
	alpha: number,
): void {
	const target = new THREE.Color(targetHex);
	color.lerp(target, alpha);
}

function lerpPositionToTuple(
	position: THREE.Vector3,
	target: [number, number, number],
	alpha: number,
): boolean {
	const targetVector = new THREE.Vector3(...target);
	position.lerp(targetVector, alpha);
	return position.distanceTo(targetVector) < LERP_SETTLED_DISTANCE_M;
}

/**
 * Declarative lighting rig matching the original Viewer3D setup:
 * ambient + hemisphere + 3-point directional (key/fill/rim) + interior point light.
 *
 * Supports smooth continuous time-of-day transitions via per-frame lerp.
 */
export function SceneLighting({
	runtimeProfile,
	timeOfDay,
	isTimeOfDayScrubbing = false,
	modelBounds = null,
	modelRevision = 0,
	shadowReceiverY = 0,
	interiorLighting = true,
}: SceneLightingProps) {
	const keyLightRef = useRef<THREE.DirectionalLight>(null);
	const fillLightRef = useRef<THREE.DirectionalLight>(null);
	const rimLightRef = useRef<THREE.DirectionalLight>(null);
	const ambientRef = useRef<THREE.AmbientLight>(null);
	const hemisphereRef = useRef<THREE.HemisphereLight>(null);
	const interiorRef = useRef<THREE.PointLight>(null);

	const glReadonly = useThree((s) => s.gl);
	const invalidate = useThree((s) => s.invalidate);
	const glRef = useRef<THREE.WebGLRenderer | null>(null);
	const isAnimatingRef = useRef(false);
	const prevTargetKeyRef = useRef("");

	useEffect(() => {
		glRef.current = glReadonly;
	}, [glReadonly]);

	const scene = useThree((s) => s.scene);
	// Re-measured on every rebuild. Keyed on the revision, not the bounds:
	// moving the benches or heater leaves the envelope (and so the deduped
	// bounds object) unchanged, and the glow would stay at the old furniture.
	const interiorGlowAnchor = useMemo(() => {
		void modelRevision;
		return planInteriorGlowAnchor(scene);
	}, [scene, modelRevision]);
	const target = useMemo(() => {
		const base = getTimeOfDayLightingTarget(timeOfDay, runtimeProfile);
		if (!interiorLighting) base.interiorGlowIntensity = 0;
		return interiorGlowAnchor
			? { ...base, interiorGlowPosition: interiorGlowAnchor }
			: base;
	}, [timeOfDay, runtimeProfile, interiorGlowAnchor, interiorLighting]);

	useEffect(() => {
		const targetKey = JSON.stringify(target);
		if (prevTargetKeyRef.current !== targetKey) {
			prevTargetKeyRef.current = targetKey;
			isAnimatingRef.current = true;
			invalidate();
		}
	}, [target, invalidate]);

	useEffect(() => {
		const light = keyLightRef.current;
		if (!light?.shadow) return;
		// Penumbra softness and the constant depth bias. The bias is kept
		// small and negative: the rig's thin wall assemblies (vapour
		// barrier, battens, panel) leak light through at 0, and a larger
		// magnitude peter-pans the plinths off the ground. The fit below
		// owns everything that varies — the ortho box and normalBias.
		light.shadow.radius = 4;
		light.shadow.blurSamples = 12;
		light.shadow.bias = -0.0002;
	}, []);

	/**
	 * Re-fit the key light's shadow camera to the model plus its ground
	 * projection, for whichever pose the caller hands it. Runs on model
	 * rebuild (dirty-tracking, via `modelBounds`), on time-of-day change,
	 * and while the lerp moves the key — never unconditionally per frame.
	 */
	const lastFitPositionRef = useRef<THREE.Vector3 | null>(null);
	const refitShadowFrustum = useCallback(
		(lightPosition: readonly [number, number, number]) => {
			const light = keyLightRef.current;
			if (!light?.shadow) return;
			applyShadowFrustum(
				light,
				fitDirectionalShadowFrustum({
					bounds: modelBounds,
					lightPosition,
					lightTarget: light.target.position.toArray(),
					groundY: shadowReceiverY,
					shadowMapSize: runtimeProfile.shadowMapSize,
				}),
			);
			lastFitPositionRef.current = new THREE.Vector3(...lightPosition);
		},
		[modelBounds, runtimeProfile.shadowMapSize, shadowReceiverY],
	);

	// Fit the mode's own pose whenever the model or the time of day
	// changes; the drift check in the lerp below covers the way there.
	useEffect(() => {
		refitShadowFrustum(target.keyPosition);
		invalidate();
	}, [refitShadowFrustum, invalidate, target.keyPosition]);

	useFrame(() => {
		if (!isAnimatingRef.current && !isTimeOfDayScrubbing) return;

		const alpha = isTimeOfDayScrubbing ? 0.24 : 0.045;
		let settled = true;
		const close = (a: number, b: number) => Math.abs(a - b) < 0.001;

		if (ambientRef.current) {
			ambientRef.current.intensity = lerpNum(
				ambientRef.current.intensity,
				target.ambientIntensity,
				alpha,
			);
			lerpColorToHex(ambientRef.current.color, target.ambientColor, alpha);
			if (!close(ambientRef.current.intensity, target.ambientIntensity))
				settled = false;
		}

		if (hemisphereRef.current) {
			hemisphereRef.current.intensity = lerpNum(
				hemisphereRef.current.intensity,
				target.hemisphereIntensity,
				alpha,
			);
			lerpColorToHex(
				hemisphereRef.current.color,
				target.hemisphereTopColor,
				alpha,
			);
			lerpColorToHex(
				hemisphereRef.current.groundColor,
				target.hemisphereBottomColor,
				alpha,
			);
			if (!close(hemisphereRef.current.intensity, target.hemisphereIntensity))
				settled = false;
		}

		if (keyLightRef.current) {
			keyLightRef.current.intensity = lerpNum(
				keyLightRef.current.intensity,
				target.keyIntensity,
				alpha,
			);
			lerpColorToHex(keyLightRef.current.color, target.keyColor, alpha);
			if (
				!lerpPositionToTuple(
					keyLightRef.current.position,
					target.keyPosition,
					alpha,
				)
			)
				settled = false;
			if (!close(keyLightRef.current.intensity, target.keyIntensity))
				settled = false;
		}

		if (fillLightRef.current) {
			fillLightRef.current.intensity = lerpNum(
				fillLightRef.current.intensity,
				target.fillIntensity,
				alpha,
			);
			lerpColorToHex(fillLightRef.current.color, target.fillColor, alpha);
			if (
				!lerpPositionToTuple(
					fillLightRef.current.position,
					target.fillPosition,
					alpha,
				)
			)
				settled = false;
			if (!close(fillLightRef.current.intensity, target.fillIntensity))
				settled = false;
		}

		if (rimLightRef.current) {
			rimLightRef.current.intensity = lerpNum(
				rimLightRef.current.intensity,
				target.rimIntensity,
				alpha,
			);
			lerpColorToHex(rimLightRef.current.color, target.rimColor, alpha);
			if (
				!lerpPositionToTuple(
					rimLightRef.current.position,
					target.rimPosition,
					alpha,
				)
			)
				settled = false;
			if (!close(rimLightRef.current.intensity, target.rimIntensity))
				settled = false;
		}

		if (interiorRef.current) {
			interiorRef.current.intensity = lerpNum(
				interiorRef.current.intensity,
				target.interiorGlowIntensity,
				alpha,
			);
			lerpColorToHex(
				interiorRef.current.color,
				target.interiorGlowColor,
				alpha,
			);
			if (
				!lerpPositionToTuple(
					interiorRef.current.position,
					target.interiorGlowPosition,
					alpha,
				)
			)
				settled = false;
			if (!close(interiorRef.current.intensity, target.interiorGlowIntensity))
				settled = false;
		}

		const renderer = glRef.current;
		if (renderer) {
			const targetExposure =
				target.toneMappingExposure * TONE_MAPPING_EXPOSURE_COMPENSATION;
			renderer.toneMappingExposure = lerpNum(
				renderer.toneMappingExposure,
				targetExposure,
				alpha,
			);
			if (!close(renderer.toneMappingExposure, targetExposure)) settled = false;
		}

		// The key light has moved; the ortho box fitted for its previous
		// pose would clip the shadow for the rest of the transition. This
		// rides the frame callback that is already running for the lerp —
		// it adds no loop of its own, and it stops the moment the
		// transition settles. On the settling frame the threshold drops
		// to the band `lerpPositionToTuple` calls settled, so the final
		// pose gets an exact fit without refitting every frame.
		const keyLight = keyLightRef.current;
		if (keyLight) {
			const lastFit = lastFitPositionRef.current;
			const drift = lastFit
				? lastFit.distanceTo(keyLight.position)
				: Number.POSITIVE_INFINITY;
			if (
				drift > (settled ? LERP_SETTLED_DISTANCE_M : SHADOW_REFIT_DISTANCE_M)
			) {
				refitShadowFrustum(keyLight.position.toArray());
			}
		}

		if (settled && !isTimeOfDayScrubbing) {
			isAnimatingRef.current = false;
		}
		invalidate();
	});

	useEffect(() => {
		invalidate();
	}, [timeOfDay, invalidate]);

	return (
		<>
			<ambientLight
				ref={ambientRef}
				color={target.ambientColor}
				intensity={target.ambientIntensity}
			/>
			<hemisphereLight
				ref={hemisphereRef}
				args={[
					target.hemisphereTopColor,
					target.hemisphereBottomColor,
					target.hemisphereIntensity,
				]}
			/>
			<directionalLight
				ref={keyLightRef}
				color={target.keyColor}
				intensity={target.keyIntensity}
				position={target.keyPosition}
				castShadow
				shadow-mapSize-width={runtimeProfile.shadowMapSize}
				shadow-mapSize-height={runtimeProfile.shadowMapSize}
			/>
			<directionalLight
				ref={fillLightRef}
				color={target.fillColor}
				intensity={target.fillIntensity}
				position={target.fillPosition}
			/>
			<directionalLight
				ref={rimLightRef}
				color={target.rimColor}
				intensity={target.rimIntensity}
				position={target.rimPosition}
			/>
			<pointLight
				ref={interiorRef}
				color={target.interiorGlowColor}
				intensity={target.interiorGlowIntensity}
				distance={7}
				decay={2}
				position={target.interiorGlowPosition}
			/>
		</>
	);
}
